/**
 * Edge Validator
 *
 * Validates workflow edges:
 * - Valid source and target references
 * - No self-referencing edges
 * - Proper conditional handles
 */

import {
  WorkflowNodeJSON,
  WorkflowEdgeJSON,
  WorkflowJSONValidationError,
  WorkflowJSONValidationWarning,
  CONDITIONAL_NODE_TYPES,
  WorkflowNodeType,
} from '../types';

/**
 * Result of edge validation
 */
export interface EdgeValidationResult {
  errors: WorkflowJSONValidationError[];
  warnings: WorkflowJSONValidationWarning[];
}

/**
 * Validate all edges in a workflow
 */
export function validateEdges(
  nodes: WorkflowNodeJSON[],
  edges: WorkflowEdgeJSON[]
): EdgeValidationResult {
  const errors: WorkflowJSONValidationError[] = [];
  const warnings: WorkflowJSONValidationWarning[] = [];

  if (!edges || edges.length === 0) {
    return { errors, warnings };
  }

  // Build set of valid node IDs
  const validNodeIds = new Set(nodes.map((n) => n.id));
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  edges.forEach((edge, index) => {
    // Validate edge has required fields
    const fieldErrors = validateEdgeRequiredFields(edge, index);
    errors.push(...fieldErrors);

    // Skip further validation if missing required fields
    if (fieldErrors.length > 0) {
      return;
    }

    // Validate source node exists
    if (!validNodeIds.has(edge.source)) {
      errors.push({
        code: 'INVALID_EDGE',
        message: `Edge "${edge.id}" references non-existent source node "${edge.source}"`,
        path: `edges[${index}].source`,
        edgeId: edge.id,
        suggestion: 'Ensure the source node ID matches an existing node',
      });
    }

    // Validate target node exists
    if (!validNodeIds.has(edge.target)) {
      errors.push({
        code: 'INVALID_EDGE',
        message: `Edge "${edge.id}" references non-existent target node "${edge.target}"`,
        path: `edges[${index}].target`,
        edgeId: edge.id,
        suggestion: 'Ensure the target node ID matches an existing node',
      });
    }

    // Validate no self-referencing edges
    if (edge.source === edge.target) {
      errors.push({
        code: 'INVALID_EDGE',
        message: `Edge "${edge.id}" is self-referencing (source equals target)`,
        path: `edges[${index}]`,
        edgeId: edge.id,
        suggestion: 'Remove the self-referencing edge or connect to a different node',
      });
    }

    // Validate conditional node handles
    const sourceNode = nodeMap.get(edge.source);
    if (sourceNode && isConditionalNode(sourceNode.type)) {
      const handleResult = validateConditionalHandle(edge, sourceNode, index);
      warnings.push(...handleResult.warnings);
    }
  });

  // Check for duplicate edges (same source-target pair)
  const edgePairs = new Map<string, string[]>();
  edges.forEach((edge) => {
    const key = `${edge.source}->${edge.target}`;
    const existing = edgePairs.get(key) || [];
    existing.push(edge.id);
    edgePairs.set(key, existing);
  });

  edgePairs.forEach((edgeIds, pair) => {
    if (edgeIds.length > 1) {
      warnings.push({
        code: 'DUPLICATE_EDGES',
        message: `Multiple edges connect the same nodes: ${pair}`,
        suggestion: `Consider removing duplicate edges: ${edgeIds.join(', ')}`,
      });
    }
  });

  return { errors, warnings };
}

/**
 * Validate required fields for an edge
 */
function validateEdgeRequiredFields(
  edge: WorkflowEdgeJSON,
  index: number
): WorkflowJSONValidationError[] {
  const errors: WorkflowJSONValidationError[] = [];

  if (!edge.id || edge.id.trim() === '') {
    errors.push({
      code: 'MISSING_REQUIRED_FIELD',
      message: `Edge at index ${index} is missing required "id" field`,
      path: `edges[${index}].id`,
      suggestion: 'Add a unique "id" field to the edge',
    });
  }

  if (!edge.source || edge.source.trim() === '') {
    errors.push({
      code: 'MISSING_REQUIRED_FIELD',
      message: `Edge "${edge.id || `at index ${index}`}" is missing required "source" field`,
      path: `edges[${index}].source`,
      edgeId: edge.id,
      suggestion: 'Add a "source" field with the source node ID',
    });
  }

  if (!edge.target || edge.target.trim() === '') {
    errors.push({
      code: 'MISSING_REQUIRED_FIELD',
      message: `Edge "${edge.id || `at index ${index}`}" is missing required "target" field`,
      path: `edges[${index}].target`,
      edgeId: edge.id,
      suggestion: 'Add a "target" field with the target node ID',
    });
  }

  return errors;
}

/**
 * Check if a node type is conditional
 */
function isConditionalNode(nodeType: string | undefined): boolean {
  if (!nodeType) return false;
  return CONDITIONAL_NODE_TYPES.has(nodeType as WorkflowNodeType);
}

/**
 * Validate conditional node handle (sourceHandle should be 'true' or 'false')
 */
function validateConditionalHandle(
  edge: WorkflowEdgeJSON,
  sourceNode: WorkflowNodeJSON,
  index: number
): { warnings: WorkflowJSONValidationWarning[] } {
  const warnings: WorkflowJSONValidationWarning[] = [];

  const validHandles = ['true', 'false'];
  const nodeType = sourceNode.type;

  // For switch nodes, allow case handles
  if (nodeType === 'switch') {
    // Switch nodes can have handles like 'case-0', 'case-1', 'default'
    if (
      edge.sourceHandle &&
      !edge.sourceHandle.startsWith('case-') &&
      edge.sourceHandle !== 'default' &&
      !validHandles.includes(edge.sourceHandle)
    ) {
      warnings.push({
        code: 'INVALID_HANDLE',
        message: `Edge "${edge.id}" from switch node "${sourceNode.id}" has unexpected sourceHandle "${edge.sourceHandle}"`,
        path: `edges[${index}].sourceHandle`,
        suggestion: 'Use "case-0", "case-1", etc. or "default" for switch node handles',
      });
    }
    return { warnings };
  }

  // For if/condition nodes
  if (edge.sourceHandle && !validHandles.includes(edge.sourceHandle)) {
    warnings.push({
      code: 'INVALID_HANDLE',
      message: `Edge "${edge.id}" from conditional node "${sourceNode.id}" has invalid sourceHandle "${edge.sourceHandle}"`,
      path: `edges[${index}].sourceHandle`,
      suggestion: 'Use "true" or "false" for conditional node sourceHandle',
    });
  }

  return { warnings };
}

/**
 * Validate merge node has multiple incoming edges
 */
export function validateMergeNodes(
  nodes: WorkflowNodeJSON[],
  edges: WorkflowEdgeJSON[]
): { warnings: WorkflowJSONValidationWarning[] } {
  const warnings: WorkflowJSONValidationWarning[] = [];

  const mergeNodeTypes = new Set(['merge', 'mergeNode']);

  nodes.forEach((node, index) => {
    if (mergeNodeTypes.has(node.type)) {
      const incomingEdges = edges.filter((e) => e.target === node.id);

      if (incomingEdges.length < 2) {
        warnings.push({
          code: 'MERGE_INSUFFICIENT_INPUTS',
          message: `Merge node "${node.data?.label || node.id}" has only ${incomingEdges.length} incoming connection(s)`,
          path: `nodes[${index}]`,
          nodeId: node.id,
          suggestion: 'Merge nodes typically should have 2 or more incoming connections',
        });
      }
    }
  });

  return { warnings };
}
